import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Element, scroller } from 'react-scroll'
import { SpinnerAnimatedIcon } from 'secondstep-components'
import { formatPageCount, formatSiteCount } from 'utils/reportsHelpers'
import { TOOLTIP_LOCATIONS, TOOLTIP_PROGRAMS } from 'utils/tooltipHelpers'
import PlaybookSteps from 'components/admin/PlaybookSteps'
import {
  Bullet,
  ContentWrapper,
  CountCopy,
  CountWrapper,
  HeaderWrapper,
  LoadingWrapper,
  NameWrapper,
  PaginationButton,
  PaginationIndexContainer,
  PaginationPageWrapper,
  PaginationSiteWrapper,
  PanelWrapper,
  Pipe,
  SchoolSortButton,
  SetupProgressSortButton,
  SiteCollapsible,
  SiteListContainer,
  SiteListFooter,
  SiteListHeader,
  StyledBodyCopy,
  StyledHeaderTwo,
  StyledHr,
  SubHeaderWrapper,
  Wrapper,
} from './component.styles'
import {
  HEADER_TEXT,
  NEXT_BUTTON_TEXT,
  ORDER_BY,
  PREVIOUS_BUTTON_TEXT,
  SETUP_PROGRESS_TEXT,
  SITES_HEADER_TEXT,
  SORT_KEYS,
} from './constants'

const AdminK8PrepareMultiSiteView = ({ isFetching, sites }) => {
  const [activeIndex, setActiveIndex] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [orderBy, setOrderBy] = useState(ORDER_BY.ASC)
  const [paginatedSites, setPaginatedSites] = useState(sites)
  const [sortKey, setSortKey] = useState(SORT_KEYS[SITES_HEADER_TEXT])
  const sitesPerPage = 25
  const showPaginationButtons = sites?.length > sitesPerPage
  const siteStartIndex = (currentPage - 1) * sitesPerPage
  const siteStartCount = siteStartIndex + 1
  const siteEndIndex = currentPage * sitesPerPage
  const siteEndCount = (currentPage - 1) * sitesPerPage + paginatedSites?.length
  const totalPages = Math.ceil(sites?.length / sitesPerPage)

  const getSchoolsWithNoProgressCount = sites => {
    let noProgressCount = 0
    sites.forEach(site => {
      if (site.launchProgressNumber === 0 && site.planProgressNumber === 0) {
        noProgressCount++
      }
    })
    return noProgressCount
  }

  const handleAccordionClick = index => {
    if (activeIndex === index) {
      setActiveIndex(null)
    } else {
      setActiveIndex(index)
    }
  }

  const handleSort = option => {
    let order

    if (option === sortKey) {
      order = orderBy === 'asc' ? 'desc' : 'asc'
    } else {
      order = 'asc'
    }

    setActiveIndex(null)
    setOrderBy(order)
    setSortKey(option)
  }

  const scrollToContentWrapper = () => {
    scroller.scrollTo('content-wrapper', {
      duration: 200,
      delay: 50,
      smooth: true,
      offset: 60,
    })
  }

  useEffect(() => {
    const orderedSites = sites.sort((a, b) => {
      if (orderBy === ORDER_BY.ASC) {
        if (sortKey === 'name') {
          return a[sortKey].localeCompare(b[sortKey])
        } else {
          return a[sortKey] - b[sortKey]
        }
      } else {
        if (sortKey === 'name') {
          return b[sortKey].localeCompare(a[sortKey])
        } else {
          return b[sortKey] - a[sortKey]
        }
      }
    })
    setPaginatedSites(orderedSites?.slice(siteStartIndex, siteEndIndex))
  }, [activeIndex, currentPage, orderBy, sites, sortKey])

  useEffect(() => {
    window.history.scrollRestoration = 'manual'
    setActiveIndex(null)
  }, [])

  if (isFetching) {
    return (
      <Wrapper>
        <Element name="content-wrapper" />
        <StyledHeaderTwo>{SITES_HEADER_TEXT}</StyledHeaderTwo>
        <StyledHr />
        <LoadingWrapper>
          <SpinnerAnimatedIcon />
          <p>Loading...</p>
        </LoadingWrapper>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Element name="content-wrapper" />
      <HeaderWrapper key="launch-key-1">
        <StyledHeaderTwo>{HEADER_TEXT}</StyledHeaderTwo>
        <SubHeaderWrapper>
          <StyledBodyCopy>{`**${sites?.length} Schools**`}</StyledBodyCopy>
          {getSchoolsWithNoProgressCount(sites) > 0 && (
            <Fragment key="no-progress-count">
              <Pipe>{'|'}</Pipe>
              <Bullet />
              <StyledBodyCopy>
                {`${getSchoolsWithNoProgressCount(sites)} school${
                  sites?.length > 1 || sites.length !== 1 ? 's' : ''
                } ${
                  sites?.length > 1 || sites.length !== 1 ? `haven't` : `hasn't`
                } started`}
              </StyledBodyCopy>
            </Fragment>
          )}
        </SubHeaderWrapper>
      </HeaderWrapper>
      <StyledHr />
      <SiteListHeader>
        <SchoolSortButton
          handleOnClick={() => {
            handleSort(SORT_KEYS[SITES_HEADER_TEXT])
            scrollToContentWrapper()
          }}
          isActive={sortKey === SORT_KEYS[SITES_HEADER_TEXT]}
          isCaretUp={
            sortKey === SORT_KEYS[SITES_HEADER_TEXT]
              ? orderBy === ORDER_BY.ASC
              : false
          }
          programKey={TOOLTIP_PROGRAMS.K8}
          title={SITES_HEADER_TEXT}
          tooltipLocation={TOOLTIP_LOCATIONS.LAUNCH_SITE}
        />
        <SetupProgressSortButton
          handleOnClick={() => {
            handleSort(SORT_KEYS[SETUP_PROGRESS_TEXT])
            scrollToContentWrapper()
          }}
          isActive={sortKey === SORT_KEYS[SETUP_PROGRESS_TEXT]}
          isCaretUp={
            sortKey === SORT_KEYS[SETUP_PROGRESS_TEXT]
              ? orderBy === ORDER_BY.ASC
              : false
          }
          programKey={TOOLTIP_PROGRAMS.K8}
          title={SETUP_PROGRESS_TEXT}
        />
      </SiteListHeader>
      <SiteListContainer>
        {paginatedSites?.map((site, index) => {
          const { name, id } = site
          return (
            <SiteCollapsible
              dataTestId={`site-collapsible-${id}`}
              followActiveProp={true}
              isActive={activeIndex === index}
              key={id}
              onClick={() => handleAccordionClick(index)}
              title={
                <PanelWrapper>
                  <NameWrapper>{name}</NameWrapper>
                  <CountWrapper>
                    <CountCopy>{site.planProgressStatus}</CountCopy>
                    <span>{`${site.planProgressNumber} of 3 Done`}</span>
                  </CountWrapper>
                </PanelWrapper>
              }
            >
              {activeIndex === index && (
                <ContentWrapper dataTestId={`site-collapsible-content-${id}`}>
                  <PlaybookSteps
                    isSingleSite={false}
                    key={`prepare-${id}`}
                    showHeader={true}
                    site={site}
                  />
                </ContentWrapper>
              )}
            </SiteCollapsible>
          )
        })}
      </SiteListContainer>
      <SiteListFooter>
        {showPaginationButtons && (
          <PaginationButton
            disabled={currentPage <= 1}
            onClick={() => {
              setCurrentPage(currentPage - 1)
              scrollToContentWrapper()
            }}
          >
            {PREVIOUS_BUTTON_TEXT}
          </PaginationButton>
        )}
        <PaginationIndexContainer>
          <PaginationPageWrapper>
            {formatPageCount(currentPage, totalPages)}
          </PaginationPageWrapper>
          <PaginationSiteWrapper>
            {formatSiteCount(siteStartCount, siteEndCount, sites?.length)}
          </PaginationSiteWrapper>
        </PaginationIndexContainer>
        {showPaginationButtons && (
          <PaginationButton
            disabled={currentPage >= totalPages}
            onClick={() => {
              setCurrentPage(currentPage + 1)
              scrollToContentWrapper()
            }}
          >
            {NEXT_BUTTON_TEXT}
          </PaginationButton>
        )}
      </SiteListFooter>
    </Wrapper>
  )
}

AdminK8PrepareMultiSiteView.propTypes = {
  currentPage: PropTypes.number,
  currentSiteCount: PropTypes.number,
  getPaginatedSites: PropTypes.func,
  isFetching: PropTypes.bool,
  orderBy: PropTypes.string,
  sites: PropTypes.array,
  sitesPerPage: PropTypes.number,
  sitesPreferences: PropTypes.array,
  sortKey: PropTypes.string,
  totalPages: PropTypes.number,
  totalSites: PropTypes.number,
}

export default AdminK8PrepareMultiSiteView
